import { Component, OnInit, signal, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { Select } from 'primeng/select';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageLayoutComponent } from '../../shared/page-layout/page-layout';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ApiCatalogueService, ApiCatalogue, ApiGroup, ApiCatalogueRequest } from '../../../service/api-catalogue/api-catalogue.service';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';

@Component({
  selector: 'app-api-catalogue',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageLayoutComponent,
    Button,
    InputText,
    Tooltip,
    Select,
    IconField,
    InputIcon,
    DialogModule,
    TableModule,
    ToastModule,
    StateContainerComponent
  ],
  templateUrl: `./api-catalogue.component.html`,
  providers: [MessageService]
})
export class ApiCatalogueComponent implements OnInit {
  private apiService = inject(ApiCatalogueService);
  private messageService = inject(MessageService);

  // State
  apis = signal<ApiCatalogue[]>([]);
  filteredApis = signal<ApiCatalogue[]>([]);
  apiGroups = signal<ApiGroup[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  pageSize = 20;
  currentPage = 0;

  // Error state
  error = signal<any>(null);

  // Filters
  filterText = '';
  groupFilter: number | null = null;
  statusFilter: string | null = null;

  // Sorting
  sortField: string = '';
  sortOrder: number = 0;

  // Dialog state
  showRegisterApiDialog = false;
  isEditMode = false;
  viewMode = false;
  editingApiId: number | null = null;

  // Form model
  newApi = {
    name: '',
    environment: 'Production',
    method: 'GET',
    gatewayRoute: '',
    upstreamServiceUrl: '',
    primaryGroupId: null as number | null,
    authentication: 'OAuth 2.1 access token',
    status: 'Draft' as 'Active' | 'Draft' | 'Disabled',
    requiredScope: ''
  };

  // Dropdown options
  environmentOptions = [
    { label: 'Production', value: 'Production' },
    { label: 'UAT', value: 'UAT' },
    { label: 'Development', value: 'Development' }
  ];

  methodOptions = [
    { label: 'GET', value: 'GET' },
    { label: 'POST', value: 'POST' },
    { label: 'PUT', value: 'PUT' },
    { label: 'PATCH', value: 'PATCH' },
    { label: 'DELETE', value: 'DELETE' },
  ];

  authenticationOptions = [
    { label: 'OAuth 2.1 access token', value: 'OAuth 2.1 access token' },
    { label: 'mTLS + OAuth 2.1', value: 'mTLS + OAuth 2.1' },
    { label: 'API key', value: 'API key' },
    { label: 'mTLS', value: 'mTLS' },
    { label: 'Public', value: 'Public' }
  ];

  statusOptions = [
    { label: 'Draft', value: 'Draft' },
    { label: 'Active', value: 'Active' },
    { label: 'Disabled', value: 'Disabled' }
  ];

  statusFilterOptions = [
    { label: 'All Statuses', value: null },
    { label: 'Active', value: 'Active' },
    { label: 'Draft', value: 'Draft' },
    { label: 'Disabled', value: 'Disabled' }
  ];

  groupOptions = signal<{ label: string; value: number }[]>([]);

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (this.filteredApis().length === 0) return 'empty';
    return 'content';
  });

  readonly showClearFilters = computed(() => {
    return !!(this.filterText || this.groupFilter || this.statusFilter);
  });

  readonly emptyStateMessage = computed(() => {
    if (this.filterText || this.groupFilter || this.statusFilter) {
      return 'No APIs match your current search or filter criteria. Try adjusting your filters.';
    }
    return 'Register your first API to start managing your API catalogue.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load APIs. Please try again.';
  });

  ngOnInit() {
    this.loadApiGroups();
    this.loadApis();
  }

  loadApiGroups() {
    this.apiService.getApiGroups().subscribe({
      next: (groups) => {
        this.apiGroups.set(groups);
        this.groupOptions.set(
          groups.map(group => ({
            label: group.name,
            value: group.id
          }))
        );
      },
      error: (error) => {
        console.error('Error loading API groups:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load API groups'
        });
      }
    });
  }

  loadApis() {
    this.loading.set(true);
    this.error.set(null);
    this.apiService
      .getApis(
        this.currentPage,
        this.pageSize,
        this.filterText,
        this.groupFilter || undefined,
        this.statusFilter || undefined
      )
      .subscribe({
        next: (response) => {
          this.apis.set(response.content);
          this.filteredApis.set(response.content);
          this.totalRecords.set(response.page.totalElements);
          this.loading.set(false);
        },
        error: (error) => {
          this.error.set(error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load APIs'
          });
        }
      });
  }

  // Retry method for error state
  retryLoad() {
    this.loadApis();
  }

  clearFilters() {
    this.filterText = '';
    this.groupFilter = null;
    this.statusFilter = null;
    this.currentPage = 0;
    this.loadApis();
  }

  applyFilter() {
    this.currentPage = 0;
    this.loadApis();
  }

  onSort(event: any) {
    if (this.loading()) return;

    const field = event.field;
    
    if (this.sortField !== field) {
      this.sortField = field;
      this.sortOrder = 1;
    } else {
      if (this.sortOrder === 1) {
        this.sortOrder = -1;
      } else if (this.sortOrder === -1) {
        this.sortField = '';
        this.sortOrder = 0;
      } else {
        this.sortField = field;
        this.sortOrder = 1;
      }
    }

    const sorted = [...this.filteredApis()];
    if (this.sortField) {
      sorted.sort((a, b) => {
        const aVal = a[this.sortField as keyof ApiCatalogue];
        const bVal = b[this.sortField as keyof ApiCatalogue];
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return this.sortOrder === 1 
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        
        if (aVal < bVal) return this.sortOrder === 1 ? -1 : 1;
        if (aVal > bVal) return this.sortOrder === 1 ? 1 : -1;
        return 0;
      });
    }
    this.filteredApis.set(sorted);
  }

  onPageChange(event: any) {
    this.currentPage = event.page;
    this.pageSize = event.rows;
    this.loadApis();
  }

  getMethodClasses(method: string): string {
    const map: Record<string, string> = {
      GET: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
      POST: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
      PUT: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
      PATCH: 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',
      DELETE: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
    };
    return map[method] ?? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
  }

  getStatusClasses(status: string): string {
    const map: Record<string, string> = {
      Active: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
      Draft: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
      Disabled: 'bg-gray-200 text-gray-600 dark:bg-surface-700 dark:text-surface-300',
    };
    return map[status] ?? 'bg-gray-200 text-gray-600 dark:bg-surface-700 dark:text-surface-300';
  }

  // View API details
  viewApiDetails(api: ApiCatalogue) {
    this.viewMode = true;
    this.isEditMode = false;
    this.editingApiId = null;
    
    // Populate the form with API data
    this.newApi = {
      name: api.name,
      environment: api.environment,
      method: api.method,
      gatewayRoute: api.gatewayRoute,
      upstreamServiceUrl: api.upstreamServiceUrl,
      primaryGroupId: api.primaryGroup.id,
      authentication: api.authentication,
      status: api.status,
      requiredScope: api.requiredScope || ''
    };
    
    this.showRegisterApiDialog = true;
  }

  openRegisterApi() {
    this.viewMode = false;
    this.isEditMode = false;
    this.editingApiId = null;
    this.resetForm();
    this.showRegisterApiDialog = true;
  }

  editApi(api: ApiCatalogue) {
    this.viewMode = false;
    this.isEditMode = true;
    this.editingApiId = api.id;
    this.newApi = {
      name: api.name,
      environment: api.environment,
      method: api.method,
      gatewayRoute: api.gatewayRoute,
      upstreamServiceUrl: api.upstreamServiceUrl,
      primaryGroupId: api.primaryGroup.id,
      authentication: api.authentication,
      status: api.status,
      requiredScope: api.requiredScope || ''
    };
    this.showRegisterApiDialog = true;
  }

  resetForm() {
    this.newApi = {
      name: '',
      environment: 'Production',
      method: 'GET',
      gatewayRoute: '',
      upstreamServiceUrl: '',
      primaryGroupId: null,
      authentication: 'OAuth 2.1 access token',
      status: 'Draft',
      requiredScope: ''
    };
  }

  closeRegisterApi() {
    this.showRegisterApiDialog = false;
    this.viewMode = false;
    this.isEditMode = false;
    this.editingApiId = null;
    this.resetForm();
  }

  saveNewApi() {
    // Validate required fields
    if (!this.newApi.name.trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'API name is required'
      });
      return;
    }

    if (!this.newApi.gatewayRoute.trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Gateway route is required'
      });
      return;
    }

    if (!this.newApi.upstreamServiceUrl.trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Upstream service URL is required'
      });
      return;
    }

    if (!this.newApi.primaryGroupId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Primary API group is required'
      });
      return;
    }

    const payload: ApiCatalogueRequest = {
      name: this.newApi.name,
      environment: this.newApi.environment,
      method: this.newApi.method,
      gatewayRoute: this.newApi.gatewayRoute,
      upstreamServiceUrl: this.newApi.upstreamServiceUrl,
      primaryGroupId: this.newApi.primaryGroupId,
      authentication: this.newApi.authentication,
      status: this.newApi.status,
      requiredScope: this.newApi.requiredScope || 'Public'
    };

    this.loading.set(true);

    if (this.isEditMode && this.editingApiId) {
      // Update existing API
      this.apiService.updateApi(this.editingApiId, payload).subscribe({
        next: (response) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `API "${response.name}" updated successfully`
          });
          this.closeRegisterApi();
          this.loadApis();
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error updating API:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error?.message || 'Failed to update API'
          });
        }
      });
    } else {
      // Register new API
      this.apiService.registerApi(payload).subscribe({
        next: (response) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `API "${response.name}" registered successfully`
          });
          this.closeRegisterApi();
          this.loadApis();
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error registering API:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error?.message || 'Failed to register API'
          });
        }
      });
    }
  }
}